import { useCallback, useEffect, useRef, useState } from 'react';
import keepAwakeVideo from './assets/keep-awake.mp4';

// Older browsers can treat video playback as activity even without Screen Wake Lock.
// Playing this clip is only a compatibility attempt, not proof that a TV will stay on.
export default function useDisplayVideoFallback(enabled) {
  const videoRef = useRef(null);
  const session = useRef(null);
  const [status, setStatus] = useState('idle');
  const request = useCallback(() => session.current?.play(), []);

  useEffect(() => {
    const video = videoRef.current;
    if (!enabled || !video) { setStatus('idle'); return undefined; }
    let pending = false;
    let recoveryTimer;
    let disposed = false;
    const playing = () => {
      if (!disposed && document.visibilityState !== 'hidden') setStatus('playing');
    };
    const play = async () => {
      if (disposed || pending || document.visibilityState === 'hidden') return;
      if (!video.paused && !video.ended) return;
      pending = true;
      try {
        // Call play synchronously from remote/pointer handlers to retain user activation.
        await video.play();
        if (disposed) {
          if (!session.current) video.pause();
          return;
        }
        if (document.visibilityState === 'hidden') { video.pause(); return; }
        // Older engines return undefined; their `playing` event reports actual playback.
        if (!video.paused) playing();
      } catch (error) {
        if (!disposed) setStatus(error?.name === 'NotAllowedError' ? 'blocked' : 'unavailable');
      } finally { pending = false; }
    };
    const interrupted = () => {
      if (disposed) return;
      setStatus('idle');
      window.clearTimeout(recoveryTimer);
      if (document.visibilityState !== 'hidden') recoveryTimer = window.setTimeout(play, 1000);
    };
    const failed = () => { if (!disposed) setStatus('unavailable'); };
    const visibilityChanged = () => {
      if (document.visibilityState === 'hidden') {
        window.clearTimeout(recoveryTimer);
        video.pause();
        setStatus('idle');
      } else play();
    };
    session.current = { play };
    video.muted = true;
    video.loop = true;
    video.src = keepAwakeVideo;
    video.addEventListener('playing', playing);
    video.addEventListener('pause', interrupted);
    video.addEventListener('ended', interrupted);
    video.addEventListener('error', failed);
    document.addEventListener('visibilitychange', visibilityChanged);
    document.addEventListener('fullscreenchange', play);
    document.addEventListener('keydown', play);
    document.addEventListener('pointerdown', play);
    document.addEventListener('click', play);
    window.addEventListener('focus', play);
    window.addEventListener('pageshow', play);
    const retry = window.setInterval(play, 30000);
    play();
    return () => {
      disposed = true;
      session.current = null;
      window.clearTimeout(recoveryTimer);
      window.clearInterval(retry);
      video.removeEventListener('playing', playing);
      video.removeEventListener('pause', interrupted);
      video.removeEventListener('ended', interrupted);
      video.removeEventListener('error', failed);
      document.removeEventListener('visibilitychange', visibilityChanged);
      document.removeEventListener('fullscreenchange', play);
      document.removeEventListener('keydown', play);
      document.removeEventListener('pointerdown', play);
      document.removeEventListener('click', play);
      window.removeEventListener('focus', play);
      window.removeEventListener('pageshow', play);
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [enabled]);

  return { videoRef, status, request };
}
